import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, LogOut, Loader2, Save, MapPin, Truck, Instagram, DollarSign, Clock, Image, Package, Type } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import AdminSidebar from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDeliverySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    base_location: "Oakleigh South, VIC",
    radius_km: 15,
    delivery_fee: 15.0,
    late_fee_per_day: 40.0,
    pickup_enabled: true,
    delivery_enabled: true,
    dispatch_buffer_days: 1
  });
  const [returnLabel, setReturnLabel] = useState({
    enabled: true,
    title: "Prepaid Return Label Included",
    description: "Your prepaid Australia Post return label will be included in your delivery box. Simply place the outfit back in the box, tape on the label, and hand it over at your local Australia Post counter to get it scanned."
  });
  const [savingLabel, setSavingLabel] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    verifyAndFetch(token);
  }, [navigate]);

  const verifyAndFetch = async (token) => {
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      fetchSettings();
    } catch (error) {
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    }
  };

  const fetchSettings = async () => {
    try {
      const [deliveryRes, labelRes] = await Promise.all([
        axios.get(`${API}/settings/delivery`),
        axios.get(`${API}/settings/return-label`),
      ]);
      setFormData({
        base_location: deliveryRes.data.base_location || "Oakleigh South, VIC",
        radius_km: deliveryRes.data.radius_km || 15,
        delivery_fee: deliveryRes.data.delivery_fee || 15.0,
        late_fee_per_day: deliveryRes.data.late_fee_per_day || 40.0,
        pickup_enabled: deliveryRes.data.pickup_enabled !== false,
        delivery_enabled: deliveryRes.data.delivery_enabled !== false,
        dispatch_buffer_days: deliveryRes.data.dispatch_buffer_days || 1
      });
      if (labelRes.data) {
        setReturnLabel({
          enabled: labelRes.data.enabled !== false,
          title: labelRes.data.title || "",
          description: labelRes.data.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.radius_km < 1 || formData.radius_km > 100) {
      toast.error("Radius must be between 1 and 100 km");
      return;
    }
    if (formData.delivery_fee < 0) {
      toast.error("Delivery fee cannot be negative");
      return;
    }
    if (formData.late_fee_per_day < 0) {
      toast.error("Late fee cannot be negative");
      return;
    }
    if (!formData.pickup_enabled && !formData.delivery_enabled) {
      toast.error("At least one delivery method must be enabled");
      return;
    }
    
    try {
      setSaving(true);
      await Promise.all([
        axios.put(`${API}/settings/delivery`, formData, getAuthHeaders()),
        axios.put(`${API}/settings/return-label`, returnLabel, getAuthHeaders()),
      ]);
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex">
      {/* Sidebar */}
      <AdminSidebar />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-2xl">
          <div className="mb-8">
            <h1 className="text-2xl text-[#676664] font-light mb-2">Delivery Settings</h1>
            <p className="text-sm text-[#676664]/60">Configure your delivery options, service area, and late fees</p>
          </div>

          <form onSubmit={handleSave} className="space-y-8">
            {/* Service Area */}
            <div className="bg-white p-6 border border-[#282828]/10">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-[#9A7B4F]" />
                <h2 className="text-sm text-[#676664] font-medium tracking-wide">Service Area</h2>
              </div>
              
              <div className="space-y-5">
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Base Location</Label>
                  <Input
                    value={formData.base_location}
                    onChange={(e) => setFormData(p => ({ ...p, base_location: e.target.value }))}
                    placeholder="Oakleigh South, VIC"
                    className="mt-2 border-[#282828]/10"
                    data-testid="base-location-input"
                  />
                  <p className="text-[10px] text-[#676664]/40 mt-1">This is displayed to customers and used as reference</p>
                </div>
                
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Service Radius (km)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.radius_km}
                    onChange={(e) => setFormData(p => ({ ...p, radius_km: parseInt(e.target.value) || 15 }))}
                    className="mt-2 border-[#282828]/10 w-32"
                    data-testid="radius-input"
                  />
                  <p className="text-[10px] text-[#676664]/40 mt-1">Delivery available within this radius from base location</p>
                </div>
              </div>
            </div>

            {/* Delivery Options */}
            <div className="bg-white p-6 border border-[#282828]/10">
              <div className="flex items-center gap-3 mb-6">
                <Truck className="w-5 h-5 text-[#9A7B4F]" />
                <h2 className="text-sm text-[#676664] font-medium tracking-wide">Delivery Options</h2>
              </div>
              
              <div className="space-y-5">
                {/* Pickup Toggle */}
                <div className="flex items-center justify-between py-3 border-b border-[#282828]/10">
                  <div>
                    <Label className="text-[#676664] text-sm">Local Pickup (Free)</Label>
                    <p className="text-[10px] text-[#676664]/50">Customers can collect by appointment</p>
                  </div>
                  <Switch
                    checked={formData.pickup_enabled}
                    onCheckedChange={(checked) => setFormData(p => ({ ...p, pickup_enabled: checked }))}
                    data-testid="pickup-enabled-switch"
                  />
                </div>
                
                {/* Delivery Toggle */}
                <div className="flex items-center justify-between py-3 border-b border-[#282828]/10">
                  <div>
                    <Label className="text-[#676664] text-sm">Local Delivery</Label>
                    <p className="text-[10px] text-[#676664]/50">Deliver within service area</p>
                  </div>
                  <Switch
                    checked={formData.delivery_enabled}
                    onCheckedChange={(checked) => setFormData(p => ({ ...p, delivery_enabled: checked }))}
                    data-testid="delivery-enabled-switch"
                  />
                </div>
                
                {/* Delivery Fee */}
                {formData.delivery_enabled && (
                  <div className="animate-fadeIn">
                    <Label className="text-[#676664]/60 text-[11px]">Delivery Fee ($AUD)</Label>
                    <div className="relative mt-2">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.delivery_fee}
                        onChange={(e) => setFormData(p => ({ ...p, delivery_fee: parseFloat(e.target.value) || 0 }))}
                        className="pl-9 border-[#282828]/10 w-32"
                        data-testid="delivery-fee-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Late Fees */}
            <div className="bg-white p-6 border border-[#282828]/10">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-5 h-5 text-[#C4A4A0]" />
                <h2 className="text-sm text-[#676664] font-medium tracking-wide">Late Return Policy</h2>
              </div>
              
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Late Fee Per Day ($AUD)</Label>
                <div className="relative mt-2">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.late_fee_per_day}
                    onChange={(e) => setFormData(p => ({ ...p, late_fee_per_day: parseFloat(e.target.value) || 0 }))}
                    className="pl-9 border-[#282828]/10 w-32"
                    data-testid="late-fee-input"
                  />
                </div>
                <p className="text-[10px] text-[#676664]/40 mt-2">
                  Charged per day for items returned after the due date. Deducted from the customer's security bond*.
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#B8C4B8]/20 p-5 rounded-lg">
              <h3 className="text-sm text-[#3D2B1F] font-medium mb-2">How it works</h3>
              <ul className="text-[11px] text-[#3D2B1F]/70 space-y-1 font-light">
                <li>• Outfits are dispatched {formData.dispatch_buffer_days} day{formData.dispatch_buffer_days > 1 ? "s" : ""} before the event date</li>
                <li>• Customers return by the day after their event</li>
                <li>• We allow 1 day cleaning buffer before the next booking</li>
                <li>• Late fees are automatically calculated and deducted from security bonds*</li>
              </ul>
            </div>

            {/* Dispatch Buffer */}
            <div className="bg-[#FAF8F0] p-5 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                <h3 className="text-sm text-[#3D2B1F] font-medium">Dispatch Buffer</h3>
              </div>
              <p className="text-[10px] text-[#676664]/50 mb-4">How many days before the event should outfits be dispatched?</p>
              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant={formData.dispatch_buffer_days === 1 ? "default" : "outline"} 
                  onClick={() => setFormData(p => ({ ...p, dispatch_buffer_days: 1 }))}
                  className={formData.dispatch_buffer_days === 1 ? "bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" : "border-[#282828]/10"}
                  data-testid="dispatch-1day-btn"
                >
                  1 Day Before
                </Button>
                <Button 
                  type="button"
                  variant={formData.dispatch_buffer_days === 2 ? "default" : "outline"} 
                  onClick={() => setFormData(p => ({ ...p, dispatch_buffer_days: 2 }))}
                  className={formData.dispatch_buffer_days === 2 ? "bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" : "border-[#282828]/10"}
                  data-testid="dispatch-2day-btn"
                >
                  2 Days Before
                </Button>
              </div>
            </div>

            {/* Return Label Config */}
            <div className="bg-white p-6 border border-[#282828]/10">
              <div className="flex items-center gap-3 mb-6">
                <Package className="w-5 h-5 text-[#6B8F71]" />
                <h2 className="text-sm text-[#676664] font-medium tracking-wide">Return Label Section</h2>
              </div>
              
              <div className="space-y-5">
                <div className="flex items-center justify-between py-3 border-b border-[#282828]/10">
                  <div>
                    <Label className="text-[#676664] text-sm">Show on Booking Confirmation</Label>
                    <p className="text-[10px] text-[#676664]/50">Display the return label info on the success page after checkout</p>
                  </div>
                  <Switch
                    checked={returnLabel.enabled}
                    onCheckedChange={(checked) => setReturnLabel(p => ({ ...p, enabled: checked }))}
                    data-testid="return-label-enabled-switch"
                  />
                </div>
                
                {returnLabel.enabled && (
                  <div className="space-y-4 animate-fadeIn">
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Section Title</Label>
                      <Input
                        value={returnLabel.title}
                        onChange={(e) => setReturnLabel(p => ({ ...p, title: e.target.value }))}
                        placeholder="Prepaid Return Label Included"
                        className="mt-2 border-[#282828]/10"
                        data-testid="return-label-title-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Description</Label>
                      <textarea
                        value={returnLabel.description}
                        onChange={(e) => setReturnLabel(p => ({ ...p, description: e.target.value }))}
                        rows={3}
                        className="mt-2 w-full border border-[#282828]/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#9A7B4F] resize-none"
                        placeholder="Enter the return label description shown to customers..."
                        data-testid="return-label-description-input"
                      />
                    </div>
                    {/* Preview */}
                    <div className="bg-[#B8C4B8]/10 border border-[#B8C4B8]/20 p-4">
                      <p className="text-[9px] text-[#676664]/40 tracking-[0.15em] uppercase mb-2">Preview</p>
                      <div className="flex items-start gap-3">
                        <Package className="w-5 h-5 text-[#6B8F71] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[13px] text-[#282828] font-medium mb-1">{returnLabel.title || "Title"}</p>
                          <p className="text-[11px] text-[#676664]/60 leading-relaxed">{returnLabel.description || "Description"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8"
              data-testid="save-delivery-settings-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
